import { skipToken } from '@apollo/client/react'
import { useAtomValue } from 'jotai'
import type { ReactNode } from 'react'
import { memo, useMemo, useState } from 'react'
import { Button } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import type { ListTagsQueryVariables } from 'src/services/apollo/generated/graphql'
import { useGetTackByCodeName } from 'src/services/apollo/tack'
import { useListTags } from 'src/services/apollo/tag'
import { OrderOption } from 'src/services/helpers/orderOptionParams'
import { fetchTackGallery } from 'src/services/orpc/tack'
import { localeAtom } from 'src/stores/locale'
import { cx } from 'src/utils/cx'
import { getNodesFromConnection } from 'src/utils/graphql'
import { useQs } from 'src/utils/qs'
import { getLocalizedTackName } from 'src/utils/tack'
import { track } from 'src/utils/tracker'
import { useContestConfig } from '../Contest/useContestConfig'
import { HorizontalScrollable } from '../Scrollable/Scrollable'
import { feedToolbarButtonStyles } from '../ui/FeedToolbarButton'
import type { TagGalleryItemProps } from './TagGalleryItem'
import TagGalleryItem from './TagGalleryItem'

type TagLikeItem = {
  icon: JSX.Element
  name: string
  displayName: string
  selected?: boolean
  onClick?: () => void
}

export interface TagGalleryProps {
  scene?: string
  className?: string
  /**
   * For navigating to /tags/:tagName
   * Otherwise, selecting a tag will update qs directly
   * @param identifier - The tag name or tack codeName
   * @returns
   */
  navigateToTag?: (identifier: string) => string
  navigateToContest?: (args: {
    id: string
    cid?: number
    active: boolean
  }) => string
  /**
   * Selected tag name
   * Will read tag from qs by default
   */
  currentTag?: string
  displayUnknownTag?: boolean

  /**
   * Looks like tags
   * But actually aren't
   */
  prependTagAlikeItems?: TagLikeItem[]

  /** If true, contestItems will be placed before startTagAlikeItems  */
  contestItemsStayFirst?: boolean

  /**
   * Arbitrary elements to prepend before all items (including prependTagAlikeItems)
   * Useful for adding OrderSelect or other controls on mobile
   */
  prependElements?: ReactNode
}

const TagGallery = memo((props: TagGalleryProps) => {
  const {
    className,
    scene,
    prependTagAlikeItems = [],
    contestItemsStayFirst = false,
    displayUnknownTag,
    navigateToTag,
    navigateToContest,
    prependElements,
  } = props
  const { t } = useTranslation('common')
  const contestConfig = useContestConfig()

  const [qs, setQs] = useQs()
  const { tag: tagFromQuery, challenge } = qs

  const currentTag = props.currentTag || tagFromQuery

  const locale = useAtomValue(localeAtom)

  const { data: tags = [] } = useSWR(['listTagsOrTacks', locale], () =>
    fetchTackGallery({ first: 50, language: locale }),
  )

  const currentTagKnown = useMemo(() => {
    return tags.some(t => t.codeName === currentTag)
  }, [currentTag, tags])

  // Fetch tack data for unknown tack to get media information
  const { data: currentTackData } = useGetTackByCodeName(
    !currentTagKnown && props.displayUnknownTag && currentTag
      ? currentTag
      : skipToken,
  )

  const contestItems = useMemo(() => {
    const items: ReactNode[] = []
    for (const cfg of contestConfig) {
      const { id, name, cid, mediaId, isInProgress } = cfg
      if (!isInProgress) continue

      const active = currentTag === 'challenge' && +challenge === cid
      const displayName = t(`common:contest.tag.${name}.name`, {
        defaultValue: name,
      })

      const tagItemProps: TagGalleryItemProps = {
        displayName,
        active,
        name,
        mediaId,
      }
      if (navigateToContest) {
        tagItemProps.to = navigateToContest({ id, cid, active })
      }
      items.push(<TagGalleryItem key={name} {...tagItemProps} />)
    }
    return items
  }, [contestConfig, currentTag, challenge, navigateToContest, t])

  const prependItems = prependTagAlikeItems.map(item => (
    <TagGalleryItem
      key={item.name}
      name={item.name}
      displayName={item.displayName}
      onClick={item.onClick}
      active={item.selected}
      icon={item.icon}
    />
  ))

  const renderItems = useMemo(() => {
    let items: ReactNode[] = []
    if (contestItemsStayFirst) {
      items = [...contestItems, ...prependItems]
    } else {
      items = [...prependItems, ...contestItems]
    }

    // add tags if loaded
    if (tags.length > 0) {
      // Add unknown tack first if needed
      if (!currentTagKnown && displayUnknownTag && currentTackData) {
        const localizedName = getLocalizedTackName(
          currentTackData.tackTerms,
          currentTackData.defaultName,
          locale,
        )
        const active = currentTackData.codeName === currentTag
        const tagItemProps: TagGalleryItemProps = {
          name: localizedName,
          active,
          scene,
          mediaId: currentTackData.mediaId ?? undefined,
        }

        if (navigateToTag) {
          tagItemProps.to = navigateToTag(currentTackData.codeName)
        } else {
          tagItemProps.onClick = () => {
            setQs({
              tag: active ? null : currentTackData.codeName,
              challenge: null,
            })
          }
        }

        items.push(
          <TagGalleryItem key={currentTackData.id} {...tagItemProps} />,
        )
      }

      // Add all tags
      for (const tag of tags) {
        // For tacks, use codeName; for tags, use name
        const identifier = tag.codeName || tag.name
        const active = identifier === currentTag
        const tagItemProps: TagGalleryItemProps = {
          name: tag.name,
          active,
          scene,
          mediaId: tag.mediaId ?? undefined,
          thumbnailUrl: tag.thumbnailUrl,
        }

        if (navigateToTag) {
          tagItemProps.to = navigateToTag(identifier)
        } else {
          tagItemProps.onClick = () => {
            setQs({
              tag: active ? null : identifier,
              challenge: null,
            })
          }
        }

        items.push(<TagGalleryItem key={tag.id} {...tagItemProps} />)
      }
    } else if (items.length === 0) {
      // Add loading skeleton items
      for (let index = 0; index < 10; index++) {
        items.push(
          <div
            key={index}
            className="min-w-[80px] flex gap-1 items-center rounded-lg h-10"
          >
            <div className="w-[24px] h-[24px] rounded-full bg-skeleton animate-pulse"></div>
            <div className="rounded w-[40px] animate-pulse h-4 bg-skeleton"></div>
          </div>,
        )
      }
    }

    return items
    // remove contestItems, currentTagKnown as they're derived from other deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contestItemsStayFirst,
    contestConfig,
    prependItems,
    tags,
    currentTag,
    currentTackData,
    scene,
    displayUnknownTag,
    navigateToTag,
    setQs,
    locale,
  ])

  return (
    <div
      className={cx('w-full flex flex-col', className)}
      id="tag-gallery"
      style={{ scrollMarginTop: 'var(--app-header-height)' }}
    >
      <HorizontalScrollable className="flex min-w-full items-center gap-1">
        {prependElements}
        {renderItems}
      </HorizontalScrollable>
    </div>
  )
})
TagGallery.displayName = 'memo(TagGallery)'

export default TagGallery

export const OrderAndTagGallery = (props: {
  className?: string
  listTagVariables?: ListTagsQueryVariables
  onchange: (type: 'tag' | 'order', value: string) => void
}) => {
  const { className, listTagVariables, onchange } = props
  const { t } = useTranslation('common')

  const [selected, setSelected] = useState<{
    tag?: string
    order?: string
  }>({ order: OrderOption.trending })

  const { data, loading } = useListTags({
    ...listTagVariables,
    first: 50,
    orderBy: '-weight',
  })

  const tags = useMemo(() => getNodesFromConnection(data), [data])

  return (
    <div
      className={cx('w-full flex flex-col', className)}
      id="tag-gallery"
      style={{ scrollMarginTop: 'var(--app-header-height)' }}
    >
      <HorizontalScrollable className="flex min-w-full gap-2">
        <NewTagGalleryItem
          key={OrderOption.trending}
          displayName={t('common:order-option.trending')}
          active={selected.order === OrderOption.trending}
          onClick={() => {
            setSelected({
              order: OrderOption.trending,
              tag: undefined,
            })
            onchange('order', OrderOption.trending)
          }}
        />

        <NewTagGalleryItem
          key={OrderOption.mostLiked}
          displayName={t('common:order-option.most-liked')}
          active={selected.order === OrderOption.mostLiked}
          onClick={() => {
            setSelected({
              order: OrderOption.mostLiked,
              tag: undefined,
            })
            onchange('order', OrderOption.mostLiked)
          }}
        />
        {tags.length > 0
          ? tags.map(tag => {
              const active =
                tag.name === selected.tag && selected.order === undefined
              return (
                <NewTagGalleryItem
                  key={tag.id}
                  displayName={tag.name}
                  active={active}
                  onClick={() => {
                    track('tag_click', {
                      tag: tag.name,
                      isCancel: active,
                      scene: 'home',
                    })
                    setSelected({
                      order: undefined,
                      tag: tag.name,
                    })
                    onchange('tag', tag.name)
                  }}
                />
              )
            })
          : loading
            ? Array.from({ length: 10 }).map((_, index) => {
                return (
                  <div
                    key={index}
                    className="min-w-[80px] flex gap-1 items-center rounded-lg px-1.5 py-1"
                  >
                    <div className="w-[24px] h-[24px] rounded-full bg-skeleton animate-pulse"></div>
                    <div className="rounded w-[40px] animate-pulse h-4 bg-skeleton"></div>
                  </div>
                )
              })
            : null}
      </HorizontalScrollable>
    </div>
  )
}

const NewTagGalleryItem = (props: {
  active: boolean
  displayName: string
  onClick?: () => any
}) => {
  const { active, displayName, onClick } = props
  return (
    <Button
      className={feedToolbarButtonStyles({
        active,
        class: 'px-4',
      })}
      onPress={() => onClick?.()}
      type="button"
    >
      <div
        className={cx(
          'text-sm max-w-full flex justify-center whitespace-nowrap',
          active ? 'text-white' : 'text-inherit',
        )}
        title={displayName}
      >
        {displayName}
      </div>
    </Button>
  )
}
